import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import CachedImage from '@/components/CachedImage';
import PrimaryButton from '@/components/PrimaryButton';
import SecondaryButton from '@/components/SecondaryButton';

export default function EmailSuccessScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { email } = useLocalSearchParams<{ email?: string }>();
  const [loading, setLoading] = useState(false);

  const handleResendEmail = async () => {
    if (!email) {
      Alert.alert('Erro', 'E-mail não encontrado');
      return;
    }

    setLoading(true);

    try {
      console.log('🔁 Resending magic link for:', email);

      const encodedEmail = encodeURIComponent(email.toLowerCase().trim());
      const response = await fetch(`https://mobile.padrinho.app/api/email/send-password-reset?first_access=true&email=${encodedEmail}&terms_accepted=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          purpose: 'first-access'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Resend magic link error:', data);
        Alert.alert('Erro', data.message || 'Erro ao reenviar link. Tente novamente.');
        return;
      }

      console.log('✅ Magic link resent successfully');
      Alert.alert('Link Reenviado!', 'O e-mail de acesso foi reenviado. Verifique sua caixa de entrada.');

    } catch (error) {
      console.error('❌ Unexpected error during resend:', error);
      Alert.alert('Erro', 'Erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWelcome = () => {
    router.replace('/welcome');
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Background decorative elements */}
      <Svg 
        width="856" 
        height="1243" 
        viewBox="0 0 856 1243" 
        fill="none" 
        style={styles.backgroundScribble}
      >
        <Path 
          d="M378.35 14.8169C727.792 409.9 -289.154 692.305 457.832 597.701C732.799 562.877 691.222 786.257 630.91 868.637C589.421 925.306 542.993 870.277 594.769 838.687C629.934 817.232 693.457 808.049 734.094 831.912C801.683 871.603 792.939 942.812 707.51 976.079C555.689 1035.2 546.244 1131.64 565.369 1192.03" 
          stroke="#F0EEE4" 
          strokeWidth="24" 
          strokeLinecap="round" 
        />
      </Svg>

      <View style={styles.backgroundBlur} />

      <Svg 
        width="181" 
        height="149" 
        viewBox="0 0 181 149" 
        fill="none" 
        style={styles.scribbleLeft}
      >
        <Path 
          d="M0.324219 41.2898C18.2494 69.1687 60.5469 74.6879 88.0329 44.5317C125.641 3.27001 69.6633 -23.802 59.3707 30.2443C48.1774 89.0205 84.7439 155.006 180.49 147.791" 
          stroke="#F0EEE4" 
          strokeWidth="24" 
          strokeLinecap="round" 
        />
      </Svg>

      <Svg 
        width="124" 
        height="125" 
        viewBox="0 0 124 125" 
        fill="none" 
        style={styles.scribbleRight}
      >
        <Path 
          d="M32.4297 10.9868L54.7767 13.7622" 
          stroke="#F0EEE4" 
          strokeWidth="24" 
          strokeLinecap="round" 
        />
        <Path 
          d="M114.182 91.8022L113.214 71.499" 
          stroke="#F0EEE4" 
          strokeWidth="24" 
          strokeLinecap="round" 
        />
        <Path 
          d="M41.1806 84.2986L70.5968 55.8377" 
          stroke="#F0EEE4" 
          strokeWidth="24" 
          strokeLinecap="round" 
        />
      </Svg>

      {/* Main content */}
      <View style={styles.body}>
        <View style={styles.content}>
          <View style={styles.imageContainer}>
            <CachedImage
              source={require('../assets/images/email-delivery.png')}
              style={styles.emailDeliveryImage}
              resizeMode="contain"
            />
          </View>

          <View style={styles.textContainer}>
            <Text style={styles.title}>
              {email ? `${email.split('@')[0]}, te enviamos um e-mail para acesso ao Padrinho` : 'Te enviamos um e-mail para acesso ao Padrinho'}
            </Text>
            <Text style={styles.subtitle}>
              Cheque sua caixa de entrada para continuar
            </Text>
          </View>
        </View>

        <View style={[styles.buttonSection, { paddingBottom: insets.bottom + 24 }]}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator color="#015480" />
              <Text style={styles.loadingText}>Reenviando e-mail...</Text>
            </View>
          ) : (
            <>
              <PrimaryButton
                title="Reenviar e-mail de acesso"
                onPress={handleResendEmail}
                disabled={loading}
              />
              <SecondaryButton
                title="Voltar para o início"
                onPress={handleBackToWelcome}
                disabled={loading}
              />
            </>
          )}
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3', // Colors-Bege-Spring-Wood
  },

  // Background decorative elements
  backgroundScribble: {
    position: 'absolute',
    bottom: -200,
    left: -310,
    transform: [{ rotate: '67.573deg' }],
  },
  backgroundBlur: {
    position: 'absolute',
    top: 197,
    right: -33,
    width: 459,
    height: 459,
    backgroundColor: '#F9F8F3',
    borderRadius: 229,
    opacity: 0.5,
  },
  scribbleLeft: {
    position: 'absolute',
    bottom: 64,
    left: -5,
  },
  scribbleRight: {
    position: 'absolute',
    top: 79,
    left: 232,
    transform: [{ rotate: '-45deg' }],
  },

  // Main content
  body: {
    flex: 1,
    paddingHorizontal: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
    width: '100%',
    flex: 1,
  },

  // Image
  imageContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 280,
    height: 209,
  },
  emailDeliveryImage: {
    width: 280,
    height: 280,
  },

  // Text content
  textContainer: {
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  title: {
    color: '#015480', // Colors-Blue-Blumine
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 29, // 120% of 24px
  },
  subtitle: {
    color: '#151515', // Colors-Neutral-Bunker
    textAlign: 'center',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20, // 140% of 14px
  },

  // Buttons
  buttonSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
  },
  loadingContainer: {
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  loadingText: {
    color: '#666666',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
  },
});